import React, { useState, useEffect, useRef } from "react";
import { Button } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { get, post } from "util/requests";
import { DataGrid } from "@mui/x-data-grid";
import styles from "../corelab.module.css";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import BreadCrumbs from "../components/bread_crumbs";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import AddIcon from "@mui/icons-material/Add";
import NewFolderModal from "../components/new_folder_modal";
import CircleIcon from "@mui/icons-material/Circle";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import SelectedFileButtons from "ui/buttons/selected_file_buttons";
import moment from "moment";
import { handleFileAddedToQueue } from "ui/upload_status/helpers";
import DeleteAssetModal from "../components/delete_asset_modal";
import ContainsDataNoDeleteModal from "../components/contains_data_no_delete_modal";
import { arrayHaveCommonItems } from "util/helpers";
import ReplaceModal from "../components/replace_modal";
import RenameModal from "../components/rename_modal";
import InvalidExtensionModal from "../components/invalid_extension_modal";
import FlagsModal from "../components/flags_modal";
import { ThemeProvider } from "@mui/material";
import { theme, corelab_api_url } from "config";
import colors from "colors";
import Tag from "ui/general/tag";
import { FilterIcon, FilterIconActive } from "ui/icons";
import FileFolderToggle from "ui/general/file_folder_toggle";
import FlagFilterModal from "./flag_filter_modal";

export default function FileListUI(props) {
  const { from_module_bar = null, subject_id = null, toggleViewMode } = props;

  const [data, setData] = useState(null);
  const [location, setLocation] = useState([]);
  const [addFolderIsOpen, setAddFolderIsOpen] = useState(false);
  const [deleteAssetIsOpen, setDeleteAssetIsOpen] = useState(false);
  const [containsDataIsOpen, setContainsDataIsOpen] = useState(false);
  const [replaceModalIsOpen, setReplaceModalIsOpen] = useState(false);
  const [renameModalIsOpen, setRenameModalIsOpen] = useState(false);
  const [showFlagsModal, setShowFlagsModal] = useState(false);
  const [showFlagFiltersModal, setShowFlagFiltersModal] = useState(false);
  const [filters, setFilters] = useState(null);
  const [invalidExtensionModalIsOpen, setInvalidExtensionModalIsOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const file_select_ref = useRef(null);
  const dispatch = useDispatch();
  const refresh_file_list = useSelector((state) => state.files.refresh_file_list);
  const file_queue = useSelector((state) => state.files.files);

  const study_id = useSelector((state) => state.study.active_study_id);

  const get_current_location_uuid = () => location.slice(-1)[0]?.uuid;

  const fetch_file_list = () => {
    setData(null);
    get(`${corelab_api_url}/file_list`, {
      params: {
        subject_id,
        study_id,
        uuid: get_current_location_uuid() || null,
        flagged: (filters?._true || []).join(",") || undefined,
        not_flagged: (filters?._false || []).join(",") || undefined,
      },
    }).then((ret) => {
      setData(ret.data);
    });
  };

  useEffect(() => {
    fetch_file_list();
  }, [refresh_file_list, subject_id, filters]);

  const fetch_path = (obj, direction = "down") => {
    let _location;
    if (direction === "down") {
      _location = location;
      _location.push({ ...obj, clickable: true });
    } else {
      let index = location.findIndex((crumb) => crumb.uuid === obj.uuid);
      if (index === 0 || index === -1) {
        _location = location.slice(0, location.length - 1);
      } else {
        _location = location.slice(0, index + 1);
      }
    }

    setLocation(_location);
    setSelectedFiles([]);
    get(`${corelab_api_url}/file_data`, {
      params: { uuid: _location.slice(-1)[0].uuid, study_id },
    }).then((ret) => {
      setData(ret.data);
      setSelectedFiles([]);
    });
  };

  const createNewFolder = (folder_name) => {
    let uuid = location.slice(-1)[0].uuid;
    post(`${corelab_api_url}/folder`, { uuid, folder_name, subject_id }).then((ret) => {
      let new_folder = ret.data;
      fetch_file_list();
      setAddFolderIsOpen(false);
    });
  };

  const toggleFileSelection = ({ uuid, type, name, aws_key, flags, parent_uuid }) => {
    let is_selected = selectedFiles.map((sf) => sf.uuid).includes(uuid);
    let _selectedFiles = [...selectedFiles];
    if (is_selected) {
      _selectedFiles = _selectedFiles.filter((sf) => sf.uuid !== uuid);
    } else {
      _selectedFiles.push({ uuid, type, name, aws_key, flags, parent_uuid });
    }
    setSelectedFiles(_selectedFiles);
  };

  const deleteAssets = () => {
    post(`${corelab_api_url}/delete_assets`, {
      list: selectedFiles,
      parent_uuid: get_current_location_uuid(),
    }).then(() => {
      setDeleteAssetIsOpen(false);
      fetch_file_list();
      setSelectedFiles([]);
    });
  };

  const initDelete = () => {
    post(`${corelab_api_url}/check_can_delete`, {
      list: selectedFiles,
      parent_uuid: get_current_location_uuid(),
    }).then((ret) => {
      if (ret.data.can_delete) {
        setDeleteAssetIsOpen(true);
      } else {
        setContainsDataIsOpen(true);
      }
    });
  };
  const handleModifyFlags = (uuid, flags) => {
    return new Promise((resolve) => {
      post(`${corelab_api_url}/file_flags/${uuid}`, { flags }).then(() => {
        let file = selectedFiles.find((f) => f.uuid === uuid);
        file.flags = flags;
        setSelectedFiles([...selectedFiles]);
        let _data = [...data];
        file = _data.find((f) => f.uuid === uuid);
        file.flags = flags;
        setData([..._data]);
        resolve();
      });
    });
  };

  return (
    <div className={styles.wrapper}>
      <input
        key={Date.now()}
        ref={file_select_ref}
        type="file"
        onChange={(event) => {
          if (event.target.files.length) {
            let extension = Array.from(event.target.files)
              .map((f) => f.name)[0]
              .split(".")
              .slice(-1)[0];
            if (file_queue.find((f) => f.name === event.target.files[0].name && f.percentage !== 100)) {
              return;
            } else if (
              arrayHaveCommonItems(
                Array.from(event.target.files).map((f) => f.name),
                data.filter((d) => d.type === "file").map((d) => d.name)
              )
            ) {
              setReplaceModalIsOpen(event);
            } else if (!extension || extension.length < 2 || extension.length > 5) {
              setInvalidExtensionModalIsOpen(Array.from(event.target.files)[0].name);
              //checking for an invalid filename
            } else {
              handleFileAddedToQueue(dispatch, event, location.slice(-1)[0].uuid, subject_id);
            }
            // event.target.value = null;
          }
        }}
        style={{ display: "none" }}
      />
      <div className={styles.table_wrapper}>
        <div className={styles.table_header}>
          <BreadCrumbs location={location} fetch_path={fetch_path} />
          <SelectedFileButtons
            selectedFiles={selectedFiles}
            initDelete={initDelete}
            setRenameModalIsOpen={setRenameModalIsOpen}
            from_module_bar={from_module_bar}
            showFlagsModal={() => setShowFlagsModal(true)}
          />

          {from_module_bar && (
            <div className={styles.right_button_wrapper}>
              <FileFolderToggle onClick={() => toggleViewMode("folder")} mode={"file"} />
              <div>
                {filters?._true?.length || filters?._false?.length ? (
                  <FilterIconActive onClick={() => setShowFlagFiltersModal(true)} />
                ) : (
                  <FilterIcon onClick={() => setShowFlagFiltersModal(true)} />
                )}
              </div>
            </div>
          )}
        </div>
        <div
          style={{
            height: from_module_bar ? "calc(100vh - 200px)" : "calc(100vh - 300px)",
          }}
        >
          <DataGrid
            rows={data}
            columns={[
              {
                field: "selected",
                headerName: "",
                width: 30,
                headerClassName: "columnHeaders",
                renderCell: ({ row }) => (
                  <div className={styles.circle_cell}>
                    {selectedFiles.map((sf) => sf.uuid).includes(row.uuid) ? (
                      <CircleIcon sx={{ fontSize: "14px" }} />
                    ) : (
                      <CircleOutlinedIcon sx={{ fontSize: "14px" }} />
                    )}
                  </div>
                ),
              },

              {
                field: "name",
                headerName: "Name",
                width: 400,
                headerClassName: "columnHeaders",
                renderCell: ({ row }) => (
                  <div>
                    <span
                      className={styles.folder_link}
                      onClick={() => {
                        if (row.type === "folder" || row.type === "site_folder") {
                          fetch_path({
                            name: row.name,
                            uuid: row.uuid,
                          });
                        }
                      }}
                    >
                      {" "}
                      {row.name}
                    </span>
                  </div>
                ),
              },
              {
                field: "subject_id",
                headerName: "Subject ID",
                width: 120,
                headerClassName: "columnHeaders",
              },
              {
                field: "site_folder",
                headerName: "Parent Folder",
                width: 120,
                headerClassName: "columnHeaders",
              },
              {
                field: "modified_on",
                headerName: "Modified",
                type: "number",
                width: 150,
                align: "center",
                headerAlign: "center",
                headerClassName: "columnHeaders",
                renderCell: ({ row }) => <div>{moment(row.modified_on).format("MMMM DD, YYYY")}</div>,
                sortComparator: (d1, d2) => moment(d1) - moment(d2),
              },
              {
                field: "flags",
                headerName: "Flags",
                type: "text",
                sortable: false,
                minWidth: 400,
                align: "center",
                headerAlign: "center",
                headerClassName: "columnHeaders",
                renderCell: ({ row }) => (
                  <div className={styles.tag_wrapper}>
                    {row.flags?.map((f) => (
                      <Tag text={f.toUpperCase()} />
                    ))}
                  </div>
                ),
              },
            ]}
            disableColumnSelector
            disableRowSelectionOnClick
            disableColumnMenu
            hideFooterSelectedRowCount
            columnHeaderHeight={34}
            rowHeight={34}
            sx={{
              boxShadow: 0,
              border: 1,
              borderColor: "#f0f0f0",
              "& .MuiDataGrid-cell:hover": {
                color: "#8349fd",
                cursor: "pointer",
              },
              "& .MuiDataGrid-topContainer .MuiDataGrid-columnHeader ": {
                // backgroundColor: "#f7f3fe",
                backgroundColor: colors.table_header_background,
              },
              "& .MuiDataGrid-topContainer .MuiDataGrid-filler ": {
                // backgroundColor: "#f7f3fe",
                backgroundColor: colors.table_header_background,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                color: "#333",
              },
            }}
            onRowClick={(row) => {
              // if (row.row.type === "folder") {
              //   fetch_path({ name: row.row.name, uuid: row.row.uuid });
              // }
              toggleFileSelection(row.row);
            }}
            onCellClick={(cell) => {
              // if (cell.field === "name" && cell.row.type === "folder") {
              //   fetch_path({ name: cell.row.name, uuid: cell.row.uuid });
              // }
            }}
            loading={data === null}
            slotProps={{
              loadingOverlay: {
                variant: "skeleton",
                noRowsVariant: "skeleton",
              },
            }}
          />
        </div>
      </div>
      <NewFolderModal
        isOpen={addFolderIsOpen}
        close={() => setAddFolderIsOpen(false)}
        onYes={createNewFolder}
        data={data}
      />
      <DeleteAssetModal
        isOpen={deleteAssetIsOpen}
        close={() => setDeleteAssetIsOpen(false)}
        onYes={deleteAssets}
        selectedFiles={selectedFiles}
      />
      <ContainsDataNoDeleteModal
        isOpen={containsDataIsOpen}
        close={() => setContainsDataIsOpen(false)}
        selectedFiles={selectedFiles}
      />

      <ReplaceModal
        isOpen={replaceModalIsOpen}
        close={() => setReplaceModalIsOpen(false)}
        onYes={() => {
          let event = replaceModalIsOpen; //we stored it in the state
          handleFileAddedToQueue(dispatch, replaceModalIsOpen, get_current_location_uuid(), subject_id, true);
          setReplaceModalIsOpen(false);
          event.target.value = null;
        }}
      />
      <RenameModal
        data={selectedFiles}
        allFilesList={data}
        isOpen={renameModalIsOpen}
        close={() => setRenameModalIsOpen(false)}
        onYes={(re_fetch, new_name) => {
          setRenameModalIsOpen(false);
          if (re_fetch) {
            fetch_file_list();
            let _selectedFiles = [...selectedFiles];
            _selectedFiles[0].name = new_name;
            setSelectedFiles(_selectedFiles);
          }
        }}
      />
      <InvalidExtensionModal
        openData={invalidExtensionModalIsOpen}
        close={() => setInvalidExtensionModalIsOpen(false)}
      />
      <FlagsModal
        selectedFile={selectedFiles[0] || {}}
        isOpen={showFlagsModal}
        close={() => setShowFlagsModal(false)}
        handleModifyFlags={handleModifyFlags}
      />
      <FlagFilterModal
        selectedFile={selectedFiles[0] || {}}
        isOpen={showFlagFiltersModal}
        close={() => setShowFlagFiltersModal(false)}
        handleModifyFlags={handleModifyFlags}
        clear={() => {
          setFilters(null);
          setShowFlagFiltersModal(false);
        }}
        filters={filters}
        setFilters={(filters) => {
          setFilters(filters);
          setShowFlagFiltersModal(false);
        }}
      />
    </div>
  );
}
